import { useState } from 'react';
import { Plus, Trash2, Search, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API, deviceTypes, allDeviceTypes, vehicleCategories, buildDurationBreakdown } from '../utils';

const InstallationsSection = ({ installations, addInstallation, removeInstallation, updateInstallation, calculateTotalDuration, formData, setFormData }) => {
  const isProvisional = formData.confirmation_status === "provisional";
  const isCA = formData.intervention_type === 'ca';
  const isDeinstall = formData.intervention_type === 'uninstall' || formData.intervention_type === 'deinstallation';
  const requireDeviceFields = !isProvisional && !isCA;
  // Per le disinstallazioni la "Consegna Dispositivo" non ha senso (richiesta Veronica 2026-07-07)
  const requireDelivery = requireDeviceFields && !isDeinstall;
  const [searchingJob, setSearchingJob] = useState({});

  const addExtraJob = (installationId) => {
    const inst = installations.find(i => i.id === installationId);
    if (!inst) return;
    const extraJobs = inst.extra_jobs || [];
    updateInstallation(installationId, 'extra_jobs', [
      ...extraJobs,
      { id: Date.now().toString(), job_number: '', device_type: '' }
    ]);
  };

  const removeExtraJob = (installationId, jobId) => {
    const inst = installations.find(i => i.id === installationId);
    if (!inst) return;
    updateInstallation(installationId, 'extra_jobs', (inst.extra_jobs || []).filter(j => j.id !== jobId));
  };

  const updateExtraJob = (installationId, jobId, field, value) => {
    const inst = installations.find(i => i.id === installationId);
    if (!inst) return;
    updateInstallation(installationId, 'extra_jobs', (inst.extra_jobs || []).map(j =>
      j.id === jobId ? { ...j, [field]: value } : j
    ));
  };

  const searchSfJob = async (installationId, jobId, jobNumber) => {
    if (!jobNumber.trim()) return;
    const key = `${installationId}-${jobId}`;
    setSearchingJob(prev => ({ ...prev, [key]: true }));
    try {
      const res = await axios.get(`${API}/salesforce/job/${encodeURIComponent(jobNumber.trim())}`);
      if (res.data) {
        if (jobId === 'main') {
          if (res.data.sf_job_name) updateInstallation(installationId, 'job_number', res.data.sf_job_name);
          if (res.data.vehicle_make) updateInstallation(installationId, 'vehicle_brand', res.data.vehicle_make);
          if (res.data.vehicle_model) updateInstallation(installationId, 'vehicle_model', res.data.vehicle_model);
          if (res.data.vehicle_vrm) updateInstallation(installationId, 'license_plate', res.data.vehicle_vrm);
          if (res.data.vehicle_vin) updateInstallation(installationId, 'chassis_number', res.data.vehicle_vin);
          const dtMap = { 'SVR': 'SVR', 'TELEMATICS': 'OBD', 'GENERIC': 'SVR' };
          if (res.data.product_group && dtMap[res.data.product_group]) {
            updateInstallation(installationId, 'device_type', dtMap[res.data.product_group]);
          }
        } else {
          updateExtraJob(installationId, jobId, 'job_number', res.data.sf_job_name || jobNumber);
          const dtMap = { 'SVR': 'SVR', 'TELEMATICS': 'OBD', 'GENERIC': 'SVR' };
          if (res.data.product_group && dtMap[res.data.product_group]) {
            updateExtraJob(installationId, jobId, 'device_type', dtMap[res.data.product_group]);
          }
        }
      }
    } catch (e) {
      // Silently fail - user can type manually
    } finally {
      setSearchingJob(prev => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div className="space-y-4" data-testid="installations-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-gray-800">
          Veicoli & Installazioni
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({installations.length} {installations.length === 1 ? 'veicolo' : 'veicoli'})
          </span>
        </h3>
        {!isProvisional && (() => {
          const totalMin = calculateTotalDuration();
          const hours = Math.floor(totalMin / 60);
          const mins = totalMin % 60;
          const breakdown = buildDurationBreakdown(installations, formData.vehicle_category);
          return (
            <div className="text-right">
              <div className="text-sm font-medium text-gray-600">
                Durata totale:{' '}
                <span className="text-lojack-red font-semibold" data-testid="total-duration">
                  {hours > 0 ? `${hours}h ` : ''}{mins > 0 ? `${mins}min` : (hours === 0 ? '0 min' : '')}
                  <span className="text-gray-400 font-normal ml-1">({totalMin} min)</span>
                </span>
              </div>
              {breakdown && (
                <div className="text-xs text-gray-500 mt-0.5" data-testid="duration-breakdown" title={breakdown}>
                  {breakdown}
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Vehicle Category selector (Auto/Supercar/Moto) - applies +30min on Supercar/Moto */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="vehicle-category-selector">
        <label className="block text-xs font-semibold text-amber-800 mb-1.5">
          Tipologia Veicolo
          <span className="ml-2 font-normal text-amber-600">(Moto +1h; Supercar/Scooter +30 minuti)</span>
        </label>
        <div className="flex gap-2 flex-wrap">
          {vehicleCategories.map(cat => (
            <button
              key={cat.value}
              type="button"
              onClick={() => setFormData({ ...formData, vehicle_category: cat.value })}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition border ${
                (formData.vehicle_category || 'auto') === cat.value
                  ? 'bg-amber-500 text-white border-amber-600 shadow-sm'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-amber-100'
              }`}
              data-testid={`vehicle-category-${cat.value}`}
            >
              {cat.label}{cat.extraMinutes > 0 && <span className="ml-1 text-xs opacity-75">+{cat.extraMinutes >= 60 ? `${cat.extraMinutes / 60}h` : `${cat.extraMinutes}min`}</span>}
            </button>
          ))}
        </div>
      </div>

      {installations.map((installation, index) => (
        <div key={installation.id} className="bg-gray-50 rounded-lg p-4 mb-4 relative">
          <div className="flex items-center justify-between mb-3">
            <span className="font-medium text-gray-700">
              Veicolo {index + 1}
              {installation.device_type && (
                <span className="ml-2 text-xs text-blue-600">
                  ({allDeviceTypes.find(d => d.value === installation.device_type)?.durationLabel || ''})
                </span>
              )}
              {(installation.extra_jobs || []).length > 0 && (
                <span className="ml-2 text-xs text-purple-600">
                  + {(installation.extra_jobs || []).length} job aggiuntiv{(installation.extra_jobs || []).length === 1 ? 'o' : 'i'}
                </span>
              )}
            </span>
            {installations.length > 1 && (
              <button type="button" onClick={() => removeInstallation(installation.id)}
                className="text-red-500 hover:text-red-700 text-sm">
                Rimuovi
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Job Number with SF search */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Job Number {!isProvisional && <span className="text-red-500">*</span>}
              </label>
              <div className="flex gap-1">
                <input
                  type="text"
                  value={installation.job_number}
                  onChange={(e) => updateInstallation(installation.id, 'job_number', e.target.value)}
                  onBlur={(e) => { if (e.target.value.trim()) searchSfJob(installation.id, 'main', e.target.value); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchSfJob(installation.id, 'main', e.target.value); } }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-lojack-red"
                  placeholder="JB-5700091"
                  data-testid={`installation-job-number-${index}`}
                />
                <button type="button"
                  onClick={() => searchSfJob(installation.id, 'main', installation.job_number)}
                  disabled={searchingJob[`${installation.id}-main`]}
                  className="px-2 py-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition disabled:opacity-50"
                  title="Cerca su Salesforce">
                  {searchingJob[`${installation.id}-main`] ? <Loader2 size={14} className="animate-spin text-blue-600" /> : <Search size={14} className="text-blue-600" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Tipo Dispositivo {requireDeviceFields && <span className="text-red-500">*</span>}
              </label>
              <select
                value={installation.device_type}
                onChange={(e) => updateInstallation(installation.id, 'device_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-lojack-red"
                required={requireDeviceFields}
                data-testid={`installation-device-type-${index}`}
              >
                <option value="">Seleziona...</option>
                {deviceTypes.map((device) => (
                  <option key={device.value} value={device.value}>
                    {device.label} - {device.durationLabel}
                  </option>
                ))}
                {/* If the current value is a legacy combo, show it as deprecated so the user still sees what was set */}
                {installation.device_type && !deviceTypes.find(d => d.value === installation.device_type) && (() => {
                  const legacy = allDeviceTypes.find(d => d.value === installation.device_type);
                  return legacy ? (
                    <option value={legacy.value}>{legacy.label} - {legacy.durationLabel} (combo storica)</option>
                  ) : null;
                })()}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Consegna Dispositivo {requireDelivery && <span className="text-red-500">*</span>}
              </label>
              <select
                value={installation.device_delivery || ""}
                onChange={(e) => updateInstallation(installation.id, 'device_delivery', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-lojack-red"
                required={requireDelivery}
                data-testid={`installation-device-delivery-${index}`}
              >
                <option value="">Seleziona...</option>
                <option value="in_loco">Dispositivo in loco</option>
                <option value="anticipato_tecnico">Da anticipare dal tecnico</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tipologia SVR</label>
              <select
                value={installation.service_type || ""}
                onChange={(e) => updateInstallation(installation.id, 'service_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-lojack-red"
              >
                <option value="">Seleziona...</option>
                <option value="base">BASE</option>
                <option value="vigile">VIGILE</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Marca {!isProvisional && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={installation.vehicle_brand}
                onChange={(e) => updateInstallation(installation.id, 'vehicle_brand', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-lojack-red"
                placeholder="FIAT"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Modello {!isProvisional && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={installation.vehicle_model}
                onChange={(e) => updateInstallation(installation.id, 'vehicle_model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-lojack-red"
                placeholder="500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Targa</label>
              <input
                type="text"
                value={installation.license_plate}
                onChange={(e) => updateInstallation(installation.id, 'license_plate', e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-lojack-red"
                placeholder="AA123BB"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Telaio {!isProvisional && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={installation.chassis_number}
                onChange={(e) => updateInstallation(installation.id, 'chassis_number', e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-lojack-red"
                placeholder="WBA123..."
              />
            </div>
          </div>

          {/* Extra Jobs Section */}
          {(installation.extra_jobs || []).length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs font-semibold text-purple-700 mb-2">Job Aggiuntivi (stesso veicolo)</p>
              {(installation.extra_jobs || []).map((ej, ejIdx) => (
                <div key={ej.id} className="flex gap-2 mb-2 items-center">
                  <div className="flex-1 flex gap-2">
                    <div className="flex-1 flex gap-1">
                      <input
                        type="text"
                        value={ej.job_number}
                        onChange={(e) => updateExtraJob(installation.id, ej.id, 'job_number', e.target.value)}
                        onBlur={(e) => { if (e.target.value.trim()) searchSfJob(installation.id, ej.id, e.target.value); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchSfJob(installation.id, ej.id, e.target.value); } }}
                        className="flex-1 px-3 py-1.5 border border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 bg-white"
                        placeholder={`Job ${ejIdx + 2}`}
                        data-testid={`extra-job-${index}-${ejIdx}`}
                      />
                      <button type="button"
                        onClick={() => searchSfJob(installation.id, ej.id, ej.job_number)}
                        disabled={searchingJob[`${installation.id}-${ej.id}`]}
                        className="px-2 py-1.5 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition disabled:opacity-50"
                        title="Cerca su Salesforce">
                        {searchingJob[`${installation.id}-${ej.id}`] ? <Loader2 size={12} className="animate-spin text-purple-600" /> : <Search size={12} className="text-purple-600" />}
                      </button>
                    </div>
                    <select
                      value={ej.device_type || ""}
                      onChange={(e) => updateExtraJob(installation.id, ej.id, 'device_type', e.target.value)}
                      className="w-40 px-2 py-1.5 border border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 bg-white"
                    >
                      <option value="">Dispositivo...</option>
                      {deviceTypes.map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                  <button type="button" onClick={() => removeExtraJob(installation.id, ej.id)}
                    className="p-1.5 text-red-400 hover:text-red-600 transition">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add Extra Job Button */}
          <button type="button" onClick={() => addExtraJob(installation.id)}
            className="mt-2 w-full py-2 border border-dashed border-purple-300 rounded-lg text-purple-600 hover:border-purple-500 hover:bg-purple-50 transition flex items-center justify-center gap-1 text-sm"
            data-testid={`add-extra-job-${index}`}>
            <Plus size={14} />
            Aggiungi Job (stesso veicolo)
          </button>
        </div>
      ))}

      <button type="button" onClick={addInstallation}
        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-lojack-red hover:text-lojack-red transition flex items-center justify-center gap-2">
        <span className="text-xl">+</span>
        Aggiungi altro veicolo
      </button>
    </div>
  );
};

export default InstallationsSection;
